/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import { useUser } from "./UserContext";
import { IconButton } from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import HeaderMenu from "../../../shared/components/HeaderMenu";
import iconMap from "../../../assets/icons/Icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { COMPANY_NAME } from "src/shared/utils/brand";
import InputBox from "./InputBox";
import { useSearch } from "./SearchContext";
import { ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import Image from "src/shared/Image";
import usePermission from "../hooks/usePermission";
import { reactAppVersion } from "src/features/auth/axiosConfig";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";

type Props = {
  MenubarVisval: (data: boolean) => void;
  MenuClose: boolean;
  pageTitle?: string;
  logout: () => void;
};

function Header({ MenubarVisval, MenuClose, pageTitle, logout }: Props) {
  const { user, userConfigration } = useUser();
  const [menu, setMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const scearchRef = useRef<HTMLDivElement>(null);
  const Menu = iconMap["menu"];
  const [menubar, setMenubar] = useState<boolean>(true);
  const location = useLocation();
  const { searchTerm, setSearchTerm } = useSearch();
  const [isMobile, setIsMobile] = useState(false);
  const [profileView, setProfileView] = useState<boolean>(false);
  const profile = useRef<HTMLDivElement>(null);
  const navigation = useNavigate();
  const { hasPermission } = usePermission()
  const { formatDate, formatTime } = useDateTimeFormatter();

  useEffect(() => {
    if (MenuClose === false) setMenubar(false);
  }, [MenuClose]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1000);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenu(false);
      }
    };

    if (menu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profile.current && !profile.current.contains(event.target as Node)) {
        setProfileView(false);
      }
    };

    if (profileView) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileView]);

  const formatted = location.pathname
    .split("/")
    .filter(Boolean)
    .filter((s) => s.length < 20)
    .filter((s) => s.toLowerCase() !== "v2")
    .slice(0, 3)
    .map((s) => {
      // 🔁 Replace grocery with custom name
      if (s.toLowerCase() === "grocery") {
        return userConfigration.customBusinessName;
      }

      // 🔤 Convert snake_case / kebab-case → Title Case
      return s
        .replace(/[_-]/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    })
    .join(" - ");

  const header = location.pathname
    .split("/")
    .filter(Boolean)
    .filter((s) => s.length < 20)
    .reverse()
    .map((s) =>
      s
        .replace(/_/g, " ") // convert _ to space
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    )
    .join(" - ");

  useEffect(() => {
    setSearchTerm("");
  }, [formatted]);

  const additionalFeatures = () => {
    return (
      <div ref={menuRef} className='relative z-50 m-1 text-black'>
        <IconButton onClick={() => setMenu((prev) => !prev)}>
          <AppsIcon className='header-menu-icon' />
        </IconButton>
        {menu && (
          <div className='right-[10%] w-fit h-fit overflow-auto flex justify-center items-center z-10 absolute top-12 bg-white shadow-md rounded border-gray-100 border-4'>
            <HeaderMenu Close={(data) => setMenu(data)} />
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    MenubarVisval(menubar);
  }, [menubar]);

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = `${pageTitle ? `${pageTitle} - ` : ""}${header} - ${COMPANY_NAME}`;
  }, [header, pageTitle]);

  const allowed = hasPermission("setting_view");

  const colorSet = ThemeBasedColorHeader();

  const showSearchRoutes = [`/grocery/${reactAppVersion}/home`, `/grocery/${reactAppVersion}/orders`, `/grocery/${reactAppVersion}/ledgers`, `/grocery/${reactAppVersion}/admin`];

  const shouldShowSearch = showSearchRoutes.some((path) => location.pathname === path || location.pathname.startsWith(path + "/"));

  const getPlaceholder = () => {
    if (location.pathname.startsWith(`/grocery/${reactAppVersion}/home`)) {
      return "Find Product";
    }

    const page = formatted?.split(" - ").pop() || "Page";
    return `Search ${page}`;
  };

  return (
    <div className={`w-full px-2 flex flex-wrap justify-between items-center gap-2 ${colorSet}`}>
      <div className='flex items-center gap-3 min-w-[200px]'>
        <div onClick={() => setMenubar(!menubar)} className='cursor-pointer flex justify-center items-center'>
          <Menu />
        </div>
        <Link to={`/grocery/${reactAppVersion}/home`} className='shrink-0'>
          <Image imagePath={user?.businessLogo} company={true} className='w-14 h-14 rounded-full bg-white p-0.5 object-cover' />
        </Link>
        <h1 className='text-lg font-semibold sm:text-xl'>
          {user?.orgName ? (user.orgName.length > 8 ? `${user.orgName.slice(0, 8)}...` : user.orgName) : ""}
        </h1>
        <h1 className='text-xl font-medium '>- {formatted}</h1>
      </div>

      <div className='flex items-center gap-3 ml-auto flex-wrap justify-end'>
        {shouldShowSearch && (
          <div className='rounded-full flex items-center px-2 gap-1 relative' ref={scearchRef}>
            <InputBox
              className='rounded-lg py-1 px-2 pr-6 text-sm text-black'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={getPlaceholder()}
            />
            {searchTerm && (
              <button type='button' onClick={() => setSearchTerm("")} className='absolute right-3 text-gray-500 hover:text-gray-700'>
                ✕
              </button>
            )}
          </div>
        )}

        {!isMobile && (
          <div className='text-sm leading-tight text-left'>
            <p>Date: {formatDate(currentTime)}</p>
            <p>Time: {formatTime(currentTime)}</p>
          </div>
        )}

        {additionalFeatures()}

        <div className='text-black'>
          <div onClick={() => setProfileView(!profileView)} className='flex items-center p-2 bg-gray-300 rounded-full cursor-pointer h-14'>
            <Image imagePath={user?.profileImageUrl || ""} className='object-cover w-10 h-10 bg-white rounded-full' profile={true} />
          </div>
          {profileView && (
            <div
              className='absolute z-50 grid items-center justify-center overflow-auto bg-white border-4 border-gray-100 rounded shadow-md  w-fit h-fit top-14 right-0'
              ref={profile}
            >
              <div
                className='w-[150px] px-3 py-2 border-b-2 border-gray-200 flex justify-end hover:text-lg cursor-pointer'
                onClick={() => navigation(`/grocery/${reactAppVersion}/Profile`)}
              >
                Profile
              </div>
              {allowed && <div
                className='w-[150px] px-3 py-2 border-b-2 border-gray-200 flex justify-end hover:text-lg cursor-pointer'
                onClick={() => navigation(`/grocery/${reactAppVersion}/setting`)}
              >
                Setting
              </div>}
              <div className='w-[150px] px-3 py-2  flex justify-end cursor-pointer hover:text-xl' onClick={logout}>
                Logout
              </div>
            </div>
          )}
        </div>
        {/* {!isMobile && <img src={companyName} alt='Company Logo' className='object-contain w-24 h-auto bg-transparent sm:w-28 bg-slate-200' />} */}
      </div>
    </div>
  );
}

export default Header;
